import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AllReports.css";
import { getAllReports } from "../../../api/reportsApi";
import { useContext } from "react";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import { getUsersSummary } from "../../../api/api";
import Select from "react-select";
import { getCalendar } from "../../../api/api";
import { formatHoursWithRounding } from "../../../utils/timeUtils";

const SPECIAL_JOB_TYPES = {
  1238: { name: "Отпуск без содержания", code: "БС" },
  1236: { name: "Больничный", code: "БЛ" },
  1237: { name: "Отпуск", code: "ОТ" },
};

registerLocale("ru", ru);
setDefaultLocale("ru");

const adjustDate = (dateString) => {
  const date = new Date(dateString);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatTime = (hours) => {
  return formatHoursWithRounding(hours);
};

const checkUserAccess = (user, accessLevel) => {
  return user?.access_levels?.some((level) => level.name === accessLevel);
};

const AllReports = () => {
  const { userData } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(getStartOfCurrentWeek());
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hasAccessToViewAll, setHasAccessToViewAll] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [calendarData, setCalendarData] = useState({});

  function getStartOfCurrentWeek() {
    const now = new Date();
    const firstDayOfWeek = new Date(
      now.setDate(now.getDate() - now.getDay() + 1)
    );
    firstDayOfWeek.setHours(0, 0, 0, 0);
    return firstDayOfWeek;
  }

  useEffect(() => {
    const checkAccess = async () => {
      const allowedAccessLevels = ["АДМС", "НО", "РО", "ИК"];
      const hasAccess =
        userData?.access_levels?.some((level) =>
          allowedAccessLevels.includes(level.name)
        ) ||
        userData?.id === 63 ||
        userData?.id === 167 ||
        userData?.id === 229 ||
        userData?.id === 54 ||
        userData?.id === 12 ||
        userData?.id === 68 ||
        userData?.id === 30 ||
        userData?.id === 21 ||
        userData?.id === 131;
      setHasAccessToViewAll(hasAccess);

      setSelectedUser(userData);

      if (hasAccess) {
        try {
          setIsLoadingUsers(true);

          let allUsers = [];

          // Для АДМС и ИК - загружаем всех пользователей без фильтрации
          if (
            checkUserAccess(userData, "АДМС") ||
            checkUserAccess(userData, "ИК")
          ) {
            const usersData = await getUsersSummary({
              isActive: true,
              pageSize: 200,
            });
            allUsers = usersData.users;
          }
          // Специальные доступы по ID пользователей (как в DepartmentReports)
          else if (
            userData.id === 167 ||
            userData.id === 229 ||
            userData.id === 54 ||
            userData.id === 12 ||
            userData.id === 68 ||
            userData.id === 30 ||
            userData.id === 21
          ) {
            let allowedDepartmentIds = [];

            if (userData.id === 167 || userData.id === 229) {
              // Массив разрешенных ID отделов для пользователей 167 и 229
              allowedDepartmentIds = [62, 63, 64, 65, 66, 67, 68, 69];
            } else if (userData.id === 54) {
              // Массив разрешенных ID отделов для пользователя 54
              allowedDepartmentIds = [61, 62, 63, 64, 65, 66, 67, 68, 69];
            } else if (userData.id === 12) {
              // Массив разрешенных ID отделов для пользователя 12
              allowedDepartmentIds = [51, 52, 53, 54, 55, 56, 57, 58, 59, 60];
            } else if (userData.id === 68) {
              // Для пользователя 68: его собственные отделы + отдел 58
              const userOwnGroups =
                userData.departments?.flatMap((dept) => dept.groups || []) ||
                [];
              const userOwnGroupIds = userOwnGroups.map((group) => group.id);
              allowedDepartmentIds = [...userOwnGroupIds, 58];
            } else if (userData.id === 30) {
              // Для пользователя с ID 30 показываем отделы с ID 53 и 52
              allowedDepartmentIds = [53, 52];
            } else if (userData.id === 21) {
              allowedDepartmentIds = [60, 68, 69];
            }

            if (allowedDepartmentIds.length > 0) {
              console.log(
                `Специальный доступ для пользователя ${userData.id}, отделы:`,
                allowedDepartmentIds
              );

              // Делаем запросы для каждой группы и объединяем результаты
              const userPromises = allowedDepartmentIds.map((groupId) =>
                getUsersSummary({
                  isActive: true,
                  pageSize: 200,
                  groupId: groupId,
                })
              );

              const userResults = await Promise.all(userPromises);

              // Объединяем всех пользователей из всех групп
              const usersMap = new Map();
              userResults.forEach((result) => {
                result.users?.forEach((user) => {
                  // Убираем дубликаты по id
                  if (!usersMap.has(user.id)) {
                    usersMap.set(user.id, user);
                  }
                });
              });

              allUsers = Array.from(usersMap.values());
              console.log(
                `Загружено уникальных пользователей из ${allowedDepartmentIds.length} отделов: ${allUsers.length}`
              );
            } else {
              allUsers = [userData];
            }
          }
          // Для НО и РО - загружаем пользователей из всех их групп
          else if (
            checkUserAccess(userData, "НО") ||
            checkUserAccess(userData, "РО")
          ) {
            const allGroups =
              userData.departments?.flatMap((dept) => dept.groups || []) || [];

            if (allGroups.length === 0) {
              console.warn("У пользователя нет группы для фильтрации");
              allUsers = [userData];
            } else {
              console.log(
                `Найдено групп у пользователя: ${allGroups.length}`,
                allGroups.map((g) => ({ id: g.id, name: g.name }))
              );

              // Делаем запросы для каждой группы и объединяем результаты
              const userPromises = allGroups.map((group) =>
                getUsersSummary({
                  isActive: true,
                  pageSize: 200,
                  groupId: group.id,
                })
              );

              const userResults = await Promise.all(userPromises);

              // Объединяем всех пользователей из всех групп
              const usersMap = new Map();
              userResults.forEach((result) => {
                result.users?.forEach((user) => {
                  // Убираем дубликаты по id
                  if (!usersMap.has(user.id)) {
                    usersMap.set(user.id, user);
                  }
                });
              });

              allUsers = Array.from(usersMap.values());
              console.log(
                `Загружено уникальных пользователей из ${allGroups.length} групп: ${allUsers.length}`
              );
            }
          } else {
            // Для остальных - только сам пользователь
            allUsers = [userData];
          }

          // Дополнительная фильтрация: только сам пользователь — для тех, у кого нет прав смотреть других.
          // Пользователи 167, 229, 54, 12, 68, 30, 21 уже получили список из разрешённых отделов — не обрезаем.
          let filteredUsers = allUsers;
          const hasSpecialDepartmentAccess = [167, 229, 54, 12, 68, 30, 21].includes(
            userData?.id
          );
          if (
            !checkUserAccess(userData, "АДМС") &&
            !checkUserAccess(userData, "ИК") &&
            !checkUserAccess(userData, "НО") &&
            !checkUserAccess(userData, "РО") &&
            userData?.id !== 63 &&
            !hasSpecialDepartmentAccess
          ) {
            filteredUsers = [userData];
          }

          console.log("Отфильтрованные пользователи:", filteredUsers);
          setUsers(filteredUsers);
        } catch (error) {
          console.error("Ошибка при загрузке пользователей:", error);
          setUsers([userData]); // В случае ошибки показываем только текущего пользователя
        } finally {
          setIsLoadingUsers(false);
        }
      }
    };

    if (userData) {
      checkAccess();
    }
  }, [userData]);

  useEffect(() => {
    console.log("Текущее состояние users:", users);
    console.log("Текущее состояние hasAccessToViewAll:", hasAccessToViewAll);
    console.log("Текущее состояние userData:", userData);
  }, [users, hasAccessToViewAll, userData]);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const userId = selectedUser?.id || userData?.id;
        if (userId) {
          const weekStart = new Date(selectedWeek);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);

          const startMonth = new Date(
            weekStart.getFullYear(),
            weekStart.getMonth(),
            1
          );
          const endMonth = new Date(
            weekEnd.getFullYear(),
            weekEnd.getMonth(),
            1
          );

          const data1 = await getAllReports(
            userId,
            startMonth.toISOString().split("T")[0]
          );

          let allReports = [...data1];

          if (startMonth.getMonth() !== endMonth.getMonth()) {
            const data2 = await getAllReports(
              userId,
              endMonth.toISOString().split("T")[0]
            );
            allReports = [...allReports, ...data2];
          }

          const uniqueReports = Array.from(
            new Map(allReports.map((report) => [report.id, report])).values()
          );
          setReports(uniqueReports);
        }
      } catch (error) {
        console.error("Ошибка при загрузке отчетов:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [userData, selectedWeek, selectedUser]);

  const handleWeekChange = (date) => {
    const firstDayOfWeek = new Date(date);
    const daysToSubtract = date.getDay() === 0 ? 6 : date.getDay() - 1;
    firstDayOfWeek.setDate(date.getDate() - daysToSubtract);
    firstDayOfWeek.setHours(0, 0, 0, 0);
    setSelectedWeek(firstDayOfWeek);
    // Обновляем выбранный месяц
    setSelectedMonth(new Date(firstDayOfWeek));
  };

  const handleMonthChange = (date) => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    firstDayOfMonth.setHours(12, 0, 0, 0);
    setSelectedMonth(firstDayOfMonth);
    // Обновляем выбранную неделю на первую неделю месяца
    const firstDayOfWeek = new Date(firstDayOfMonth);
    firstDayOfWeek.setDate(
      firstDayOfWeek.getDate() - firstDayOfWeek.getDay() + 1
    );
    setSelectedWeek(firstDayOfWeek);
  };

  const getReportsForWeek = (reports, weekStart) => {
    // Нормализуем дату начала недели
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    // Группируем отчеты по дате, используя локальную дату
    const groupedReports = reports.reduce((acc, report) => {
      const reportDate = new Date(report.fact_date_start);
      reportDate.setHours(0, 0, 0, 0);

      const dateKey = reportDate.toISOString().split("T")[0];

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(report);
      return acc;
    }, {});

    // Фильтруем только отчеты в пределах выбранной недели
    return reports.filter((report) => {
      const reportDate = new Date(report.fact_date_start);
      reportDate.setHours(0, 0, 0, 0);
      return reportDate >= start && reportDate <= end;
    });
  };

  const getReportsForMonth = (reports, month) => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    return reports.filter((report) => {
      const reportDate = adjustDate(report.fact_date_start);
      const isInMonth = reportDate >= startOfMonth && reportDate <= endOfMonth;
      if (report.id === 69208) {
        console.log(`Отчет 69208 в месяце: ${isInMonth}`);
        console.log(`Дата отчета: ${reportDate}`);
        console.log(`Начало месяца: ${startOfMonth}`);
        console.log(`Конец месяца: ${endOfMonth}`);
      }
      return isInMonth;
    });
  };

  const generateMonthlyTableHeaders = (month) => {
    const daysInMonth = new Date(
      month.getFullYear(),
      month.getMonth() + 1,
      0
    ).getDate();
    const headers = ["Проект", "Вид работ"];

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(month.getFullYear(), month.getMonth(), i);
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      headers.push({
        date: i,
        isWeekend: !dayData?.workday_flag,
        eventName: dayData?.event_name,
      });
    }
    return headers;
  };

  const groupReportsByProjectAndJobType = (reports) => {
    return reports.reduce((acc, report) => {
      const key = `${report.project?.id || "undefined"}-${
        report.portal_job_type?.id || "undefined"
      }-${report.comment || ""}-${report.is_overtime ? "overtime" : "regular"}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(report);
      return acc;
    }, {});
  };

  const groupReportsByProjectAndJobTypeMonthly = (reports) => {
    return reports.reduce((acc, report) => {
      const key = `${report.project?.id || "undefined"}-${
        report.portal_job_type?.id || "undefined"
      }-${report.is_overtime ? "overtime" : "regular"}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(report);
      return acc;
    }, {});
  };

  const formatProjectName = (name) => {
    if (!name) return "Н/Д";
    return name.split("_")[0];
  };

  const formatCellValue = (hours, isWeekend, jobTypeId) => {
    if (SPECIAL_JOB_TYPES[jobTypeId]) {
      return SPECIAL_JOB_TYPES[jobTypeId].code;
    }
    if (hours > 0) {
      return formatTime(hours);
    }
    return isWeekend ? "ВЫХ" : "";
  };

  const renderWeeklyReports = () => {
    const groupedReports = groupReportsByProjectAndJobType(weeklyReports);
    const dailyTotals = new Array(7).fill(0);
    const overtimeDailyTotals = new Array(7).fill(0);
    const dailySpecialTypes = new Array(7).fill(null);

    const rows = Object.entries(groupedReports).map(([key, reports]) => {
      const dailyHours = new Array(7).fill(0);
      const dailyRemoteHours = new Array(7).fill(0);
      const overtimeFlags = new Array(7).fill(false);
      const specialWorkDays = new Array(7).fill(false);
      const dayOffFlags = new Array(7).fill(false);
      const remoteFlags = new Array(7).fill(false);
      const hasBothTypes = new Array(7).fill(false);
      const hasRemoteAndOvertime = new Array(7).fill(false);

      reports.forEach((report) => {
        const reportDate = new Date(report.fact_date_start);
        reportDate.setHours(0, 0, 0, 0);
        const weekStartDate = new Date(selectedWeek);
        weekStartDate.setHours(0, 0, 0, 0);

        const dayIndex = Math.floor(
          (reportDate.getTime() - weekStartDate.getTime()) /
            (24 * 60 * 60 * 1000)
        );

        if (dayIndex >= 0 && dayIndex < 7) {
          const hours = report.time_spent / 3600;

          if (!SPECIAL_JOB_TYPES[report.portal_job_type?.id]) {
            if (report.is_remote) {
              dailyRemoteHours[dayIndex] += hours;
              remoteFlags[dayIndex] = true;
            } else {
              dailyHours[dayIndex] += hours;
            }

            if (report.is_overtime) {
              overtimeFlags[dayIndex] = true;
              overtimeDailyTotals[dayIndex] += hours;
            }
            if (report.is_day_off) {
              dayOffFlags[dayIndex] = true;
            }

            // Проверяем, есть ли и обычные, и удаленные отчеты в один день
            if (dailyHours[dayIndex] > 0 && dailyRemoteHours[dayIndex] > 0) {
              hasBothTypes[dayIndex] = true;
            }
            // Один отчёт с флагами удалёнки и переработки — показываем ячейку в два цвета
            if (remoteFlags[dayIndex] && overtimeFlags[dayIndex]) {
              hasRemoteAndOvertime[dayIndex] = true;
            }

            dailyTotals[dayIndex] += hours;
          } else {
            dailyHours[dayIndex] = hours;
            specialWorkDays[dayIndex] = true;
            dailySpecialTypes[dayIndex] = report.portal_job_type?.id;
          }
        }
      });

      const firstReport = reports[0];

      return (
        <tr key={key}>
          <td>{formatProjectName(firstReport.project?.name)}</td>
          <td>{firstReport.portal_job_type?.name || "Н/Д"}</td>
          <td>{firstReport.comment || "Н/Д"}</td>
          {dailyHours.map((hours, index) => {
            const currentDate = new Date(
              selectedWeek.getTime() + index * 24 * 60 * 60 * 1000
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];

            // Формируем тултип
            let tooltipText = dayData?.event_name || undefined;
            if (dailyRemoteHours[index] > 0) {
              tooltipText = tooltipText
                ? `${tooltipText}\nУдаленно: ${formatTime(
                    dailyRemoteHours[index]
                  )}`
                : `Удаленно: ${formatTime(dailyRemoteHours[index])}`;
            }

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                  ${hasRemoteAndOvertime[index] ? "cell-split-remote-overtime" : ""}
                  ${!hasRemoteAndOvertime[index] && overtimeFlags[index] ? "overtime-cell_true" : ""}
                  ${dayOffFlags[index] ? "dayoff-cell_true" : ""}
                  ${hasBothTypes[index] && !hasRemoteAndOvertime[index] ? "split-cell-remote" : ""}
                  ${
                    remoteFlags[index] && !hasBothTypes[index] && !hasRemoteAndOvertime[index]
                      ? "remote-cell_true"
                      : ""
                  }
                `}
                data-tooltip={tooltipText}
              >
                {specialWorkDays[index] ? (
                  formatCellValue(
                    hours,
                    !dayData?.workday_flag,
                    firstReport.portal_job_type?.id
                  )
                ) : hasBothTypes[index] ? (
                  <>
                    <div className="normal-hours">
                      {formatCellValue(
                        dailyHours[index],
                        !dayData?.workday_flag,
                        null
                      )}
                    </div>
                    <div className="remote-hours">
                      {formatCellValue(
                        dailyRemoteHours[index],
                        !dayData?.workday_flag,
                        null
                      )}
                    </div>
                  </>
                ) : (
                  formatCellValue(
                    dailyHours[index] > 0
                      ? dailyHours[index]
                      : dailyRemoteHours[index],
                    !dayData?.workday_flag,
                    null
                  )
                )}
              </td>
            );
          })}
        </tr>
      );
    });

    return (
      <>
        {rows}
        <tr className="overtime-total-row_true">
          <td colSpan="3">Переработанные часы</td>
          {overtimeDailyTotals.map((hours, index) => {
            const currentDate = new Date(
              selectedWeek.getTime() + index * 24 * 60 * 60 * 1000
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                  ${hours > 0 ? "overtime-cell_true" : ""}
                `}
                data-tooltip={dayData?.event_name || undefined}
              >
                {hours > 0
                  ? formatTime(hours)
                  : !dayData?.workday_flag
                  ? "ВЫХ"
                  : ""}
              </td>
            );
          })}
        </tr>
        <tr className="daily-total-row">
          <td colSpan="3">Рабочие часы</td>
          {dailyTotals.map((hours, index) => {
            const currentDate = new Date(
              selectedWeek.getTime() + index * 24 * 60 * 60 * 1000
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];
            const regularHours = hours - overtimeDailyTotals[index];
            const specialType = dailySpecialTypes[index];

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                `}
                data-tooltip={dayData?.event_name || undefined}
              >
                {specialType
                  ? SPECIAL_JOB_TYPES[specialType].code
                  : hours > 0
                  ? formatTime(regularHours)
                  : !dayData?.workday_flag
                  ? "ВЫХ"
                  : ""}
              </td>
            );
          })}
        </tr>
      </>
    );
  };

  const renderMonthlyReports = () => {
    const groupedReports =
      groupReportsByProjectAndJobTypeMonthly(monthlyReports);
    const daysInMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0
    ).getDate();
    const dailyTotals = new Array(daysInMonth).fill(0);
    const overtimeDailyTotals = new Array(daysInMonth).fill(0);
    const dailySpecialTypes = new Array(daysInMonth).fill(null);
    let monthlyTotal = 0;

    const rows = Object.entries(groupedReports).map(([key, reports]) => {
      const dailyHours = new Array(daysInMonth).fill(0);
      const dailyRemoteHours = new Array(daysInMonth).fill(0);
      const overtimeFlags = new Array(daysInMonth).fill(false);
      const specialWorkDays = new Array(daysInMonth).fill(false);
      const dayOffFlags = new Array(daysInMonth).fill(false);
      const remoteFlags = new Array(daysInMonth).fill(false);
      const hasBothTypes = new Array(daysInMonth).fill(false);
      const hasRemoteAndOvertime = new Array(daysInMonth).fill(false);

      reports.forEach((report) => {
        const day = new Date(report.fact_date_start).getDate() - 1;
        const hours = report.time_spent / 3600;

        if (!SPECIAL_JOB_TYPES[report.portal_job_type?.id]) {
          if (report.is_remote) {
            dailyRemoteHours[day] += hours;
            remoteFlags[day] = true;
          } else {
            dailyHours[day] += hours;
          }

          if (report.is_overtime) {
            overtimeFlags[day] = true;
            overtimeDailyTotals[day] += hours;
          }
          if (report.is_day_off) {
            dayOffFlags[day] = true;
          }

          // Проверяем, есть ли и обычные, и удаленные отчеты в один день
          if (dailyHours[day] > 0 && dailyRemoteHours[day] > 0) {
            hasBothTypes[day] = true;
          }
          // Один отчёт с флагами удалёнки и переработки — показываем ячейку в два цвета
          if (remoteFlags[day] && overtimeFlags[day]) {
            hasRemoteAndOvertime[day] = true;
          }

          dailyTotals[day] += hours;
          monthlyTotal += hours;
        } else {
          dailyHours[day] = hours;
          specialWorkDays[day] = true;
          dailySpecialTypes[day] = report.portal_job_type?.id;
        }
      });

      const firstReport = reports[0];

      return (
        <tr key={key}>
          <td>{formatProjectName(firstReport.project?.name)}</td>
          <td>{firstReport.portal_job_type?.name || "Н/Д"}</td>
          {dailyHours.map((hours, index) => {
            const currentDate = new Date(
              selectedMonth.getFullYear(),
              selectedMonth.getMonth(),
              index + 1
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];

            // Формируем тултип
            let tooltipText = dayData?.event_name || undefined;
            if (dailyRemoteHours[index] > 0) {
              tooltipText = tooltipText
                ? `${tooltipText}\nУдаленно: ${formatTime(
                    dailyRemoteHours[index]
                  )}`
                : `Удаленно: ${formatTime(dailyRemoteHours[index])}`;
            }

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                  ${hasRemoteAndOvertime[index] ? "cell-split-remote-overtime" : ""}
                  ${!hasRemoteAndOvertime[index] && overtimeFlags[index] ? "overtime-cell_true" : ""}
                  ${dayOffFlags[index] ? "dayoff-cell_true" : ""}
                  ${hasBothTypes[index] && !hasRemoteAndOvertime[index] ? "split-cell-remote" : ""}
                  ${
                    remoteFlags[index] && !hasBothTypes[index] && !hasRemoteAndOvertime[index]
                      ? "remote-cell_true"
                      : ""
                  }
                `}
                data-tooltip={tooltipText}
              >
                {specialWorkDays[index] ? (
                  formatCellValue(
                    hours,
                    !dayData?.workday_flag,
                    firstReport.portal_job_type?.id
                  )
                ) : hasBothTypes[index] ? (
                  <>
                    <div className="normal-hours">
                      {formatCellValue(
                        dailyHours[index],
                        !dayData?.workday_flag,
                        null
                      )}
                    </div>
                    <div className="remote-hours">
                      {formatCellValue(
                        dailyRemoteHours[index],
                        !dayData?.workday_flag,
                        null
                      )}
                    </div>
                  </>
                ) : (
                  formatCellValue(
                    dailyHours[index] > 0
                      ? dailyHours[index]
                      : dailyRemoteHours[index],
                    !dayData?.workday_flag,
                    null
                  )
                )}
              </td>
            );
          })}
        </tr>
      );
    });

    const overtimeMonthlyTotal = overtimeDailyTotals.reduce(
      (acc, curr) => acc + curr,
      0
    );

    return (
      <>
        {rows}
        <tr className="overtime-total-row_true">
          <td colSpan="2">Переработанные часы</td>
          {overtimeDailyTotals.map((hours, index) => {
            const currentDate = new Date(
              selectedMonth.getFullYear(),
              selectedMonth.getMonth(),
              index + 1
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                  ${hours > 0 ? "overtime-cell_true" : ""}
                `}
                data-tooltip={dayData?.event_name || undefined}
              >
                {hours > 0
                  ? formatTime(hours)
                  : !dayData?.workday_flag
                  ? "ВЫХ"
                  : ""}
              </td>
            );
          })}
        </tr>
        <tr className="daily-total-row">
          <td colSpan="2">Рабочие часы</td>
          {dailyTotals.map((hours, index) => {
            const currentDate = new Date(
              selectedMonth.getFullYear(),
              selectedMonth.getMonth(),
              index + 1
            );
            const dateString = currentDate.toLocaleDateString("en-CA");
            const dayData = calendarData[dateString];
            const regularHours = hours - overtimeDailyTotals[index];
            const specialType = dailySpecialTypes[index];

            return (
              <td
                key={index}
                className={`
                  ${!dayData?.workday_flag ? "weekend" : ""}
                `}
                data-tooltip={dayData?.event_name || undefined}
              >
                {specialType
                  ? SPECIAL_JOB_TYPES[specialType].code
                  : hours > 0
                  ? formatTime(regularHours)
                  : !dayData?.workday_flag
                  ? "ВЫХ"
                  : ""}
              </td>
            );
          })}
        </tr>
        <tr className="monthly-total-row">
          <td colSpan="2">Итого за месяц</td>
          <td colSpan={daysInMonth}>
            <div>
              Рабочие часы: {formatTime(monthlyTotal - overtimeMonthlyTotal)}
            </div>
            <div>Переработанные часы: {formatTime(overtimeMonthlyTotal)}</div>
          </td>
        </tr>
      </>
    );
  };

  const weeklyReports = getReportsForWeek(reports, selectedWeek);
  const monthlyReports = getReportsForMonth(reports, selectedMonth);
  const monthlyTableHeaders = generateMonthlyTableHeaders(selectedMonth);

  const generateWeeklyTableHeaders = (weekStart) => {
    const headers = ["Проект", "Вид работ", "Комментарий"];
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      headers.push({
        date: currentDate.toLocaleDateString(),
        isWeekend: !dayData?.workday_flag,
        eventName: dayData?.event_name,
      });
    }
    return headers;
  };

  const weeklyTableHeaders = generateWeeklyTableHeaders(selectedWeek);

  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - (start.getDay() || 7) + 1);
    const result = [new Date(start)];
    for (let i = 1; i < 7; i++) {
      const next = new Date(start);
      next.setDate(next.getDate() + i);
      result.push(next);
    }
    return result;
  };

  const highlightWeek = (date) => {
    const weekDays = getWeekDays(selectedWeek);
    return weekDays.some((day) => day.toDateString() === date.toDateString());
  };

  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    if (!dayData) return "";

    const classes = [];
    if (!dayData.workday_flag) classes.push("highlight-weekend");
    if (dayData.event_name) classes.push("has-event");

    return classes.join(" ");
  };

  const isWeekend = (date) => {
    const dateString = date.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];
    return dayData ? !dayData.workday_flag : false;
  };

  const getLastDayOfCurrentWeek = () => {
    const today = new Date();
    const lastDay = new Date(
      today.setDate(today.getDate() + (7 - today.getDay()))
    );
    lastDay.setHours(23, 59, 59, 999);
    return lastDay;
  };

  const checkReportDate = (report) => {
    console.log(`Проверка отчета ${report.id}:`);
    console.log(`Дата отчета: ${report.fact_date_start}`);
    console.log(`Выбранная недел: ${selectedWeek}`);
    console.log(`Выбранный месяц: ${selectedMonth}`);
  };

  useEffect(() => {
    const reportToCheck = reports.find((r) => r.id === 69208);
    if (reportToCheck) {
      console.log("Отчет 69208:", reportToCheck);
      console.log(
        "Входит в выбранную неделю:",
        isInSelectedWeek(reportToCheck)
      );
      console.log(
        "Входит в выбранный месяц:",
        isInSelectedMonth(reportToCheck)
      );
    }
  }, [reports, selectedWeek, selectedMonth]);

  const isInSelectedWeek = (report) => {
    const reportDate = new Date(report.fact_date_start);
    const weekStart = new Date(selectedWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return reportDate >= weekStart && reportDate <= weekEnd;
  };

  const isInSelectedMonth = (report) => {
    const reportDate = new Date(report.fact_date_start);
    return (
      reportDate.getMonth() === selectedMonth.getMonth() &&
      reportDate.getFullYear() === selectedMonth.getFullYear()
    );
  };

  const handleUserChange = (event) => {
    const selectedUserId = event.target.value;
    const user = users.find((u) => u.id === parseInt(selectedUserId));
    setSelectedUser(user);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "392px",
      opacity: state.isLoading || state.isDisabled ? 0.7 : 1,
      cursor: state.isLoading || state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: state.isLoading ? "#E0E0E0" : "#999",
      },
    }),
    menu: (provided) => ({
      ...provided,
      width: "392px",
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    container: (provided) => ({
      ...provided,
      width: "392px",
    }),
    loadingMessage: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      color: "#666",
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: "#999",
    }),
  };

  const LoadingIndicator = () => (
    <div className="select-loading-indicator">
      <div className="select-loading-dot"></div>
      <div className="select-loading-dot"></div>
      <div className="select-loading-dot"></div>
    </div>
  );

  const loadCalendarData = useCallback(async () => {
    try {
      const currentDate = new Date();
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 6,
        1
      );
      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 2,
        0
      );

      const dateFrom = firstDay.toISOString().split("T")[0];
      const dateTo = lastDay.toISOString().split("T")[0];

      const data = await getCalendar(dateFrom, dateTo);

      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      document.documentElement.style.setProperty("--mouse-x", `${e.clientX}px`);
      document.documentElement.style.setProperty("--mouse-y", `${e.clientY}px`);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="all-reports">
      {loading ? (
        <Preloader />
      ) : (
        <>
          {hasAccessToViewAll && (
            <div className="user-selector">
              <Select
                isDisabled={isLoadingUsers}
                isLoading={isLoadingUsers}
                loadingMessage={() => "Загрузка списка сотрудников..."}
                components={{
                  LoadingIndicator,
                }}
                options={users
                  .filter(
                    (user) =>
                      !user.access_levels?.some((level) => level.name === "РО")
                  )
                  .map((user) => ({
                    value: user.id,
                    label: `${user.surname} ${user.name}`,
                  }))}
                value={
                  selectedUser
                    ? {
                        value: selectedUser.id,
                        label: `${selectedUser.surname} ${selectedUser.name}`,
                      }
                    : null
                }
                onChange={(option) =>
                  setSelectedUser(users.find((u) => u.id === option.value))
                }
                placeholder={
                  isLoadingUsers
                    ? "Загрузка пользователей..."
                    : users.length === 0
                    ? "Нет доступных пользователей"
                    : "Выберите сотрудника"
                }
                styles={customStyles}
                className="all-report_select"
                noOptionsMessage={() => "Нет доступных пользователей"}
              />
            </div>
          )}

          <div className="week-reports">
            <h2>Отчеты за неделю</h2>
            <div className="report-status-legend">
              <div className="legend-title">Статус отчета:</div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-color overtime-cell_true"></div>
                  <span>Переработка</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color dayoff-cell_true"></div>
                  <span>Отгул</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color remote-cell_true"></div>
                  <span>Удаленно</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color cell-split-remote-overtime"></div>
                  <span>Удаленная переработка</span>
                </div>
              </div>
            </div>
            <DatePicker
              selected={selectedWeek}
              onChange={handleWeekChange}
              dateFormat="dd.MM.yyyy"
              locale="ru"
              calendarStartDay={1}
              dayClassName={highlightDays}
              renderDayContents={(day, date) => {
                const dateString = date.toLocaleDateString("en-CA");
                const dayData = calendarData[dateString];
                return (
                  <div
                    className="day-content"
                    data-tooltip={dayData?.event_name || ""}
                  >
                    {day}
                  </div>
                );
              }}
              highlightDates={getWeekDays(selectedWeek)}
              className="all-report_datepicker"
              calendarClassName="custom-calendar"
              startDate={selectedWeek}
              weekStartsOn={1}
              popperClassName="all-reports-popper"
              popperPlacement="bottom-end"
              popperProps={{
                positionFixed: true,
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport",
                    },
                  },
                ],
              }}
            />
            <table>
              <thead>
                <tr>
                  {weeklyTableHeaders.map((header, index) => {
                    if (index < 3) {
                      return <th key={index}>{header}</th>;
                    }
                    return (
                      <th
                        key={index}
                        className={header.isWeekend ? "weekend" : ""}
                        data-tooltip={header.eventName || undefined}
                      >
                        {header.date}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>{renderWeeklyReports()}</tbody>
            </table>
          </div>
          <div className="month-reports">
            <h2>Отчеты за месяц</h2>
            <div className="report-status-legend">
              <div className="legend-title">Статус отчета:</div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-color overtime-cell_true"></div>
                  <span>Переработка</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color dayoff-cell_true"></div>
                  <span>Отгул</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color remote-cell_true"></div>
                  <span>Удаленно</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color cell-split-remote-overtime"></div>
                  <span>Удаленная переработка</span>
                </div>
              </div>
            </div>
            <DatePicker
              selected={selectedMonth}
              onChange={handleMonthChange}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              locale="ru"
              className="all-report_datepicker"
              calendarClassName="custom-calendar"
              popperClassName="all-reports-popper"
              popperPlacement="bottom-end"
              popperProps={{
                positionFixed: true,
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport",
                    },
                  },
                ],
              }}
            />
            <div className="month-reports__table-container">
              <table className="month-reports__table">
                <thead>
                  <tr>
                    {monthlyTableHeaders.map((header, index) => {
                      if (index < 2) {
                        return <th key={index}>{header}</th>;
                      }
                      return (
                        <th
                          key={index}
                          className={header.isWeekend ? "weekend" : ""}
                          data-tooltip={header.eventName || undefined}
                        >
                          {header.date}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>{renderMonthlyReports()}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllReports;
